import React, { useState } from "react";
import { t } from "../i18n";
import { Link, TextT, PlayCircle, CircleNotch, ListChecks, TreeStructure } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const PAGE_TYPES = ["product", "category", "homepage", "policy", "generic"];
const LANG_CODES = ["en", "fr", "es", "it", "pt", "de"];

export default function AnalyzerForm({ lang, onAudit, onBatch, loading }) {
  const [mode, setMode] = useState("url"); // url | text | batch
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [pageType, setPageType] = useState("product");
  const [outputLang, setOutputLang] = useState(lang);
  const [speed, setSpeed] = useState("fast"); // fast (Haiku 4.5) | accurate (Sonnet 4.5)

  // Batch state
  const [batchMode, setBatchMode] = useState("sitemap"); // sitemap | urls
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [urlList, setUrlList] = useState("");
  const [maxUrls, setMaxUrls] = useState(10);

  React.useEffect(() => setOutputLang(lang), [lang]);

  const submit = (e) => {
    e.preventDefault();
    if (loading) return;

    if (mode === "batch") {
      const urls = urlList
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      if (batchMode === "sitemap" && !sitemapUrl.trim()) return;
      if (batchMode === "urls" && urls.length === 0) return;
      onBatch({
        mode: batchMode,
        sitemap_url: batchMode === "sitemap" ? sitemapUrl.trim() : undefined,
        urls: batchMode === "urls" ? urls : undefined,
        language: outputLang,
        page_type: pageType,
        max_urls: Math.max(1, Math.min(50, parseInt(maxUrls, 10) || 10)),
        speed,
      });
      return;
    }

    if (mode === "url" && !url.trim()) return;
    if (mode === "text" && !text.trim()) return;
    onAudit({
      mode,
      url: mode === "url" ? url.trim() : undefined,
      text: mode === "text" ? text : undefined,
      language: outputLang,
      page_type: pageType,
      speed,
    });
  };

  const triggerCls =
    "h-10 w-full border border-neutral-900 rounded-none shadow-none bg-white text-sm text-neutral-900 px-3 focus:ring-0 focus:ring-offset-0 ma-trans hover:bg-neutral-50";
  const contentCls = "rounded-none border border-neutral-900 shadow-none";
  const itemCls = "rounded-none";

  const Tab = ({ value, icon, label, testid }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 text-xs uppercase tracking-widest font-mono ma-trans border-l border-neutral-900 first:border-l-0 ${
        mode === value
          ? "bg-neutral-900 text-white"
          : "bg-white text-neutral-900 hover:bg-neutral-100"
      }`}
      data-testid={testid}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <form
      onSubmit={submit}
      className="border border-neutral-900 bg-white"
      data-testid="analyzer-form"
    >
      {/* Mode tabs */}
      <div className="flex border-b border-neutral-900">
        <Tab value="url" icon={<Link size={14} />} label={t(lang, "input_mode_url")} testid="mode-url-tab" />
        <Tab value="text" icon={<TextT size={14} />} label={t(lang, "input_mode_text")} testid="mode-text-tab" />
        <Tab value="batch" icon={<ListChecks size={14} />} label={t(lang, "input_mode_batch")} testid="mode-batch-tab" />
      </div>

      <div className="p-6 space-y-5">
        {mode === "url" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t(lang, "url_placeholder")}
              className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm text-neutral-900 placeholder:text-neutral-400 ma-focus"
              data-testid="url-input"
              required
            />
          </div>
        )}

        {mode === "text" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
              {t(lang, "input_mode_text")}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t(lang, "text_placeholder")}
              rows={8}
              className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm text-neutral-900 placeholder:text-neutral-400 ma-focus resize-y"
              data-testid="text-input"
            />
          </div>
        )}

        {mode === "batch" && (
          <>
            <div className="flex border border-neutral-900">
              <button
                type="button"
                onClick={() => setBatchMode("sitemap")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[11px] uppercase tracking-widest font-mono ma-trans ${
                  batchMode === "sitemap"
                    ? "bg-neutral-900 text-white"
                    : "bg-white text-neutral-900 hover:bg-neutral-100"
                }`}
                data-testid="batch-mode-sitemap-tab"
              >
                <TreeStructure size={12} />
                {t(lang, "batch_mode_sitemap")}
              </button>
              <button
                type="button"
                onClick={() => setBatchMode("urls")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[11px] uppercase tracking-widest font-mono ma-trans border-l border-neutral-900 ${
                  batchMode === "urls"
                    ? "bg-neutral-900 text-white"
                    : "bg-white text-neutral-900 hover:bg-neutral-100"
                }`}
                data-testid="batch-mode-urls-tab"
              >
                <ListChecks size={12} />
                {t(lang, "batch_mode_urls")}
              </button>
            </div>

            {batchMode === "sitemap" ? (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                  Sitemap URL
                </label>
                <input
                  type="url"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  placeholder={t(lang, "sitemap_url_placeholder")}
                  className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm text-neutral-900 placeholder:text-neutral-400 ma-focus"
                  data-testid="sitemap-url-input"
                />
              </div>
            ) : (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                  URLs
                </label>
                <textarea
                  value={urlList}
                  onChange={(e) => setUrlList(e.target.value)}
                  placeholder={t(lang, "urls_placeholder")}
                  rows={6}
                  className="w-full border border-neutral-900 bg-white px-3 py-2.5 font-mono text-xs text-neutral-900 placeholder:text-neutral-400 ma-focus resize-y"
                  data-testid="urls-input"
                />
              </div>
            )}

            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "max_urls")} (1–50)
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={maxUrls}
                onChange={(e) => setMaxUrls(e.target.value)}
                className="w-32 border border-neutral-900 bg-white px-3 py-2.5 font-mono text-sm text-neutral-900 ma-focus"
                data-testid="max-urls-input"
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
              {t(lang, "page_type")}
            </label>
            <Select value={pageType} onValueChange={setPageType}>
              <SelectTrigger
                className={triggerCls}
                data-testid="page-type-select"
                aria-label={t(lang, "page_type")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={contentCls}>
                {PAGE_TYPES.map((pt) => (
                  <SelectItem
                    key={pt}
                    value={pt}
                    className={itemCls}
                    data-testid={`page-type-option-${pt}`}
                  >
                    {t(lang, `page_type_${pt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
              {t(lang, "output_language")}
            </label>
            <Select value={outputLang} onValueChange={setOutputLang}>
              <SelectTrigger
                className={`${triggerCls} font-mono`}
                data-testid="output-language-select"
                aria-label={t(lang, "output_language")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={contentCls}>
                {LANG_CODES.map((c) => (
                  <SelectItem
                    key={c}
                    value={c}
                    className={`${itemCls} font-mono`}
                    data-testid={`output-language-option-${c}`}
                  >
                    {c.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
            {t(lang, "speed_label")}
          </label>
          <div className="flex border border-neutral-900">
            <button
              type="button"
              onClick={() => setSpeed("fast")}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 px-3 py-2 text-[11px] uppercase tracking-widest font-mono ma-trans ${
                speed === "fast"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="speed-fast-button"
              title={t(lang, "speed_fast_hint")}
            >
              <span>{t(lang, "speed_fast")}</span>
              <span className="text-[9px] opacity-70 normal-case tracking-normal">{t(lang, "speed_fast_sub")}</span>
            </button>
            <button
              type="button"
              onClick={() => setSpeed("accurate")}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 px-3 py-2 text-[11px] uppercase tracking-widest font-mono ma-trans border-l border-neutral-900 ${
                speed === "accurate"
                  ? "bg-neutral-900 text-white"
                  : "bg-white text-neutral-900 hover:bg-neutral-100"
              }`}
              data-testid="speed-accurate-button"
              title={t(lang, "speed_accurate_hint")}
            >
              <span>{t(lang, "speed_accurate")}</span>
              <span className="text-[9px] opacity-70 normal-case tracking-normal">{t(lang, "speed_accurate_sub")}</span>
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 px-4 py-3 text-sm font-medium uppercase tracking-widest ma-trans"
          data-testid="run-audit-button"
        >
          {loading ? (
            <>
              <CircleNotch size={16} className="animate-spin" />
              {mode === "batch" ? t(lang, "batch_running") : t(lang, "running")}
            </>
          ) : (
            <>
              <PlayCircle size={16} weight="fill" />
              {mode === "batch" ? t(lang, "run_batch_audit") : t(lang, "run_audit")}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
